import React, { useCallback, useEffect, useMemo, useState } from "react";
import PanelTabla from "./PanelTabla";
import { getTablaMesHora, getEstaciones } from "./TablasClimaAeroLoader";
import SelectorMes from "./SelectorMes";
import {
    Box,
    Collapse,
    Fade,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
    IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import SelectorTabla from "./SelectorTabla";
import SelectorHora from "./SelectorHora";
import { DatosTablas } from "./DatosTablasClimaAero";
import SelectorAerodromo from "../../components/selectorAerodromo";
import SelectorTipoAnalisis from "../../components/selectorTipoAnalisis";
import { TableSkeleton, LoadingOverlay } from "../../components/LoadingSkeleton";
import {
    ExpandMore,
    ExpandLess,
    FilterList,
} from "@mui/icons-material";

export default function PanelTablas() {
    const [idAerodromoSel, setIdAerodromoSel] = useState(39);
    const [tipoAnalisisSel, setTipoAnalisisSel] = useState(0);
    const [tablaSelIndex, SetTablaSelIndex] = useState(0);
    const [mesSelIndex, SetMesSelIndex] = useState(0);
    const [horaSelIndex, SetHoraSelIndex] = useState(0);
    const [datosTabla, setDatosTabla] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [filtersExpanded, setFiltersExpanded] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const showMes = DatosTablas.tablasPorMes[tablaSelIndex];
    const showHora =
        tablaSelIndex >= 0 && DatosTablas.tablasPorMesHora[tablaSelIndex];
    const tablaCargada = useMemo(
        () => tablaSelIndex >= 0 && mesSelIndex >= 0 && datosTabla,
        [tablaSelIndex, mesSelIndex, datosTabla]
    );
    const estaciones = useMemo(() => getEstaciones(), []);
    const horasDisponibles = useMemo(() => {
        // Durazno y Salto: operativa de 10 a 20 UTC
        if (idAerodromoSel === 96 || idAerodromoSel === 239) {
            return Array.from({ length: 11 }, (_, i) => i + 10);
        }
        return Array.from({ length: 24 }, (_, i) => i);
    }, [idAerodromoSel]);

    useEffect(() => {
        if (!horasDisponibles.includes(horaSelIndex)) {
            SetHoraSelIndex(horasDisponibles[0] ?? 0);
        }
    }, [horasDisponibles, horaSelIndex]);

    useEffect(() => {
        setDatosTabla(null);
        setIsLoading(true);
        async function getTabla() {
            if (
                tablaSelIndex >= 0 &&
                mesSelIndex >= 0 &&
                (!DatosTablas.tablasPorMesHora[tablaSelIndex] ||
                    !DatosTablas.tablasPorMes[tablaSelIndex] ||
                    horaSelIndex >= 0)
            ) {
                try {
                    let datosTablaAux = await getTablaMesHora(
                        idAerodromoSel,
                        tablaSelIndex,
                        mesSelIndex,
                        horaSelIndex
                    );
                    setDatosTabla(datosTablaAux);
                } catch (error) {
                    console.log(error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        }
        getTabla();
    }, [tablaSelIndex, mesSelIndex, horaSelIndex, idAerodromoSel]);

    useEffect(() => {
        const idTabla =
            DatosTablas.tablaPorDefecto[idAerodromoSel][tipoAnalisisSel];
        SetTablaSelIndex(idTabla);
    }, [idAerodromoSel, tipoAnalisisSel]);

    const onChangeAerodromo = useCallback(
        (id) => {
            setIdAerodromoSel(id);
            SetTablaSelIndex(tablaSelIndex);
        },
        [tablaSelIndex]
    );

    const onTipoAnalisisChange = useCallback((tipo) => {
        setTipoAnalisisSel(tipo);
    }, []);

    const onChangeTabla = useCallback((tabla) => {
        setDatosTabla(null);
        SetTablaSelIndex(tabla);
    }, []);

    const onChangeMes = useCallback((mes) => {
        SetMesSelIndex(mes);
    }, []);

    const onChangeHora = useCallback((hora) => {
        SetHoraSelIndex(hora);
    }, []);

    const toggleFilters = () => {
        setFiltersExpanded(!filtersExpanded);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#f5f7fa",
                px: { xs: 1.5, sm: 2, md: 4 },
                py: { xs: 2, md: 3 },
            }}
        >
            {/* Filtros */}
            <Paper
                elevation={0}
                sx={{
                    mb: { xs: 2, md: 3 },
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    bgcolor: "white",
                    overflow: "hidden",
                }}
            >
                {/* Header de filtros (mobile) */}
                {isMobile && (
                    <Box
                        onClick={toggleFilters}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            cursor: "pointer",
                            bgcolor: filtersExpanded ? "#f5f5f5" : "white",
                            transition: "background-color 0.2s ease",
                            "&:hover": {
                                bgcolor: "#f0f0f0",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <FilterList sx={{ color: "#3baec7" }} />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                            >
                                Filtros
                            </Typography>
                        </Box>
                        <IconButton size="small">
                            {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                )}

                <Collapse in={!isMobile || filtersExpanded}>
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Grid
                            container
                            columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            rowSpacing={{ xs: 2, sm: 2, md: 2 }}
                            alignItems="flex-end"
                        >
                            {/* Tipo de análisis */}
                            <Grid size={12}>
                                <SelectorTipoAnalisis
                                    tipoAnalisisSel={tipoAnalisisSel}
                                    onTipoAnalisisChange={onTipoAnalisisChange}
                                />
                            </Grid>

                            {/* Todos los dropdowns en la misma fila cuando hay espacio */}
                            <Grid size={{ xs: 12, sm: 6, lg: showMes && showHora ? 3 : showMes || showHora ? 4 : 6 }}>
                                <SelectorAerodromo
                                    aerodromos={estaciones}
                                    idAerodromoSel={idAerodromoSel}
                                    onChange={onChangeAerodromo}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, lg: showMes && showHora ? 3 : showMes || showHora ? 4 : 6 }}>
                                <SelectorTabla
                                    tipoAnalisis={tipoAnalisisSel}
                                    idEstacion={idAerodromoSel}
                                    value={tablaSelIndex}
                                    onChange={onChangeTabla}
                                />
                            </Grid>

                            {showMes && (
                                <Grid size={{ xs: 12, sm: 6, lg: showHora ? 3 : 4 }}>
                                    <SelectorMes
                                        onChange={onChangeMes}
                                        value={mesSelIndex}
                                    />
                                </Grid>
                            )}

                            {showHora && (
                                <Grid size={{ xs: 12, sm: 6, lg: showMes ? 3 : 4 }}>
                                    <SelectorHora
                                        horas={horasDisponibles}
                                        onChange={onChangeHora}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Collapse>
            </Paper>

            {/* Tabla de datos */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    bgcolor: "white",
                    position: "relative",
                    minHeight: 300,
                }}
            >
                {isLoading && <LoadingOverlay message="Cargando datos..." />}

                <Fade in={!isLoading && tablaCargada} timeout={300}>
                    <Box>
                        {tablaCargada && (
                            <PanelTabla
                                titulo={datosTabla.titulo}
                                datosTabla={datosTabla.datosTabla}
                                titulosColumnas={datosTabla.titulosColumnas}
                                subtitulo={datosTabla.subtitulo}
                                totalObservaciones={datosTabla.totalObs}
                                titulosColumnas2={datosTabla.titulosColumnas2}
                                porMesHora={
                                    DatosTablas.tablasPorMesHora[tablaSelIndex]
                                }
                                tablaIndex={tablaSelIndex}
                            />
                        )}
                    </Box>
                </Fade>

                {!isLoading && !tablaCargada && (
                    <Fade in={true} timeout={300}>
                        <Box
                            sx={{
                                py: { xs: 6, md: 8 },
                                textAlign: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: "50%",
                                    bgcolor: "#e0f7fa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mx: "auto",
                                    mb: 2,
                                }}
                            >
                                <FilterList
                                    sx={{ fontSize: 32, color: "#3baec7" }}
                                />
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: "#424242",
                                    mb: 0.5,
                                }}
                            >
                                Seleccioná los filtros para cargar la tabla
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Elegí un aeródromo y el tipo de análisis para
                                continuar.
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {isLoading && !tablaCargada && (
                    <Box sx={{ pt: 2 }}>
                        <TableSkeleton rows={6} columns={4} />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
